import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

/**
 * EditCheckinFormModal - Comprehensive form to edit all check-in data
 * Displays current data and allows staff to update any field
 */
function EditCheckinFormModal({ form, onClose, onSave }) {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  // Initialize form data from existing form
  useEffect(() => {
    if (form) {
      setFormData({
        // Personal Information
        guestName: form.guestName || '',
        aliasName: form.aliasName || '',
        email: form.email || '',
        phone: form.phone || '',
        whatsappNumber: form.whatsappNumber || '',
        profession: form.profession || '',
        countryOfOrigin: form.countryOfOrigin || '',
        dateOfBirth: form.dateOfBirth ? new Date(form.dateOfBirth).toISOString().split('T')[0] : '',
        bloodGroup: form.bloodGroup || '',
        
        // Address Information
        permanentAddress: form.permanentAddress || '',
        city: form.city || '',
        state: form.state || '',
        country: form.country || '',
        pincode: form.pincode || '',
        
        // Emergency Contact
        emergencyContactName: form.emergencyContactName || '',
        emergencyContactRelation: form.emergencyContactRelation || '',
        emergencyContactAddress: form.emergencyContactAddress || '',
        emergencyContactPhone: form.emergencyContactPhone || '',
        
        // Passport/Visa Information
        passportNumber: form.passportNumber || '',
        passportCountryOfIssue: form.passportCountryOfIssue || '',
        visaType: form.visaType || '',
        visaValidTill: form.visaValidTill ? new Date(form.visaValidTill).toISOString().split('T')[0] : '',
        visaNumber: form.visaNumber || '',
        dateOfArrivalInIndia: form.dateOfArrivalInIndia ? new Date(form.dateOfArrivalInIndia).toISOString().split('T')[0] : '',
        arrivedFrom: form.arrivedFrom || '',
        proceedingTo: form.proceedingTo || '',
        mobileInIndia: form.mobileInIndia || '',
        mobileInResidentCountry: form.mobileInResidentCountry || '',
        
        // Government ID
        aadhaarNumber: form.aadhaarNumber || '',
        panNumber: form.panNumber || '',
        drivingLicenseNumber: form.drivingLicenseNumber || '',
        voterId: form.voterId || '',
        
        // Health & Experience
        oshoMeditationExperience: form.oshoMeditationExperience || '',
        healthIssues: form.healthIssues || '',
        covidVaccinationStatus: form.covidVaccinationStatus || '',
        coursesAttending: form.coursesAttending || '',
        
        // Arrival Information
        expectedArrivalDate: form.expectedArrivalDate ? new Date(form.expectedArrivalDate).toISOString().split('T')[0] : '',
        expectedArrivalTime: form.expectedArrivalTime || '',
        
        // Payment Information
        balanceAmount: form.balanceAmount || 0,
        paidAmount: form.paidAmount || 0,
        remainingBalance: form.remainingBalance || 0,
        
        // Admin Fields
        roomNumber: form.roomNumber || '',
        billNumber: form.billNumber || '',
        amountReceived: form.amountReceived || 0,
        officeNotes: form.officeNotes || '',
        status: form.status || 'INCOMPLETE',
        
        // Progress Flags
        personalInfoCompleted: form.personalInfoCompleted || false,
        addressInfoCompleted: form.addressInfoCompleted || false,
        travelInfoCompleted: form.travelInfoCompleted || false,
        documentsUploaded: form.documentsUploaded || false,
        termsAgreed: form.termsAgreed || false,
        paymentSubmitted: form.paymentSubmitted || false,
        paymentVerified: form.paymentVerified || false,
        arrivalTimeProvided: form.arrivalTimeProvided || false,
        otpVerified: form.otpVerified || false,
        
        // Terms & Conditions
        termsAccepted: form.termsAccepted || false,
        privacyAccepted: form.privacyAccepted || false,
        photoVideoPermission: form.photoVideoPermission || false,
        rulesUnderstood: form.rulesUnderstood || false,
        healthResponsibility: form.healthResponsibility || false,
        identityProofProvided: form.identityProofProvided || false,
      });
    }
  }, [form]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Prepare data - only send fields that have values
      const dataToSend = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Include if value exists and is not empty string
        if (value !== '' && value !== null && value !== undefined) {
          dataToSend[key] = value;
        }
      });
      
      await onSave(dataToSend);
    } catch (error) {
      console.error('Failed to save form:', error);
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'address', label: 'Address' },
    { id: 'emergency', label: 'Emergency Contact' },
    { id: 'travel', label: 'Travel Info' },
    { id: 'government', label: 'Government ID' },
    { id: 'health', label: 'Health & Experience' },
    { id: 'arrival', label: 'Arrival Info' },
    { id: 'payment', label: 'Payment Info' },
    { id: 'admin', label: 'Admin Fields' },
    { id: 'progress', label: 'Progress Flags' },
    { id: 'terms', label: 'Terms & Conditions' },
  ];

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #D2CDBF',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#36433B',
    marginBottom: '6px',
  };

  const fieldGroupStyle = {
    marginBottom: '20px',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '2px solid #E3DFD3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: '#1A3E2A',
              marginBottom: '4px'
            }}>
              Edit Check-in Form
            </h2>
            <div style={{ fontSize: '13px', color: '#5F6B61' }}>
              Booking ID: <strong>{form?.bookingId}</strong> • UID: <strong>{form?.uid}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#5F6B61',
              padding: '4px',
              lineHeight: 1
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Content Area */}
        <div style={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* Section Navigation */}
          <div style={{
            width: '200px',
            borderRight: '1px solid #E3DFD3',
            padding: '16px',
            overflowY: 'auto',
            flexShrink: 0
          }}>
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: '8px',
                  backgroundColor: activeSection === section.id ? '#F0F3EE' : 'transparent',
                  border: activeSection === section.id ? '1px solid #1A3E2A' : '1px solid transparent',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: activeSection === section.id ? '600' : '500',
                  color: activeSection === section.id ? '#0F2619' : '#36433B',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = '#EEEBE1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {section.label}
              </button>
            ))}
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} style={{
            flex: 1,
            padding: '24px',
            overflowY: 'auto'
          }}>
            {/* Personal Information */}
            {activeSection === 'personal' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Personal Information
                </h3>
                
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Guest Name *</label>
                  <input
                    type="text"
                    value={formData.guestName || ''}
                    onChange={(e) => handleChange('guestName', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter guest name"
                  />
                </div>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Alias Name</label>
                  <input
                    type="text"
                    value={formData.aliasName || ''}
                    onChange={(e) => handleChange('aliasName', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter alias name"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Email *</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      style={inputStyle}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Phone *</label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      style={inputStyle}
                      placeholder="+91-9876543210"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>WhatsApp Number</label>
                    <input
                      type="tel"
                      value={formData.whatsappNumber || ''}
                      onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="+91-9876543210"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Profession</label>
                    <input
                      type="text"
                      value={formData.profession || ''}
                      onChange={(e) => handleChange('profession', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter profession"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Country of Origin</label>
                    <input
                      type="text"
                      value={formData.countryOfOrigin || ''}
                      onChange={(e) => handleChange('countryOfOrigin', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter country"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth || ''}
                      onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Blood Group</label>
                    <select
                      value={formData.bloodGroup || ''}
                      onChange={(e) => handleChange('bloodGroup', e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Address Information */}
            {activeSection === 'address' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Address Information
                </h3>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Permanent Address</label>
                  <textarea
                    value={formData.permanentAddress || ''}
                    onChange={(e) => handleChange('permanentAddress', e.target.value)}
                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                    placeholder="Enter permanent address"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>City</label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => handleChange('city', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter city"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>State</label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(e) => handleChange('state', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Country</label>
                    <input
                      type="text"
                      value={formData.country || ''}
                      onChange={(e) => handleChange('country', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter country"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Pincode / Postal Code</label>
                    <input
                      type="text"
                      value={formData.pincode || ''}
                      onChange={(e) => handleChange('pincode', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter pincode"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            {activeSection === 'emergency' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Emergency Contact Information
                </h3>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Emergency Contact Name</label>
                  <input
                    type="text"
                    value={formData.emergencyContactName || ''}
                    onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter contact name"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Relation</label>
                    <input
                      type="text"
                      value={formData.emergencyContactRelation || ''}
                      onChange={(e) => handleChange('emergencyContactRelation', e.target.value)}
                      style={inputStyle}
                      placeholder="e.g., Father, Mother, Spouse"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Phone</label>
                    <input
                      type="tel"
                      value={formData.emergencyContactPhone || ''}
                      onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                      style={inputStyle}
                      placeholder="+91-9876543210"
                    />
                  </div>
                </div>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Address</label>
                  <textarea
                    value={formData.emergencyContactAddress || ''}
                    onChange={(e) => handleChange('emergencyContactAddress', e.target.value)}
                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                    placeholder="Enter emergency contact address"
                  />
                </div>
              </div>
            )}

            {/* Travel Information */}
            {activeSection === 'travel' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Travel & Passport Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Passport Number</label>
                    <input
                      type="text"
                      value={formData.passportNumber || ''}
                      onChange={(e) => handleChange('passportNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter passport number"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Country of Issue</label>
                    <input
                      type="text"
                      value={formData.passportCountryOfIssue || ''}
                      onChange={(e) => handleChange('passportCountryOfIssue', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter country"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Visa Type</label>
                    <input
                      type="text"
                      value={formData.visaType || ''}
                      onChange={(e) => handleChange('visaType', e.target.value)}
                      style={inputStyle}
                      placeholder="e.g., Tourist, Business"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Visa Number</label>
                    <input
                      type="text"
                      value={formData.visaNumber || ''}
                      onChange={(e) => handleChange('visaNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter visa number"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Visa Valid Till</label>
                    <input
                      type="date"
                      value={formData.visaValidTill || ''}
                      onChange={(e) => handleChange('visaValidTill', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Date of Arrival in India</label>
                    <input
                      type="date"
                      value={formData.dateOfArrivalInIndia || ''}
                      onChange={(e) => handleChange('dateOfArrivalInIndia', e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Arrived From</label>
                    <input
                      type="text"
                      value={formData.arrivedFrom || ''}
                      onChange={(e) => handleChange('arrivedFrom', e.target.value)}
                      style={inputStyle}
                      placeholder="City/Country"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Proceeding To</label>
                    <input
                      type="text"
                      value={formData.proceedingTo || ''}
                      onChange={(e) => handleChange('proceedingTo', e.target.value)}
                      style={inputStyle}
                      placeholder="Next destination"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Mobile in India</label>
                    <input
                      type="tel"
                      value={formData.mobileInIndia || ''}
                      onChange={(e) => handleChange('mobileInIndia', e.target.value)}
                      style={inputStyle}
                      placeholder="+91-9876543210"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Mobile in Resident Country</label>
                    <input
                      type="tel"
                      value={formData.mobileInResidentCountry || ''}
                      onChange={(e) => handleChange('mobileInResidentCountry', e.target.value)}
                      style={inputStyle}
                      placeholder="Country mobile number"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Government ID */}
            {activeSection === 'government' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Government ID Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Aadhaar Number</label>
                    <input
                      type="text"
                      value={formData.aadhaarNumber || ''}
                      onChange={(e) => handleChange('aadhaarNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter Aadhaar number"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>PAN Number</label>
                    <input
                      type="text"
                      value={formData.panNumber || ''}
                      onChange={(e) => handleChange('panNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter PAN number"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Driving License Number</label>
                    <input
                      type="text"
                      value={formData.drivingLicenseNumber || ''}
                      onChange={(e) => handleChange('drivingLicenseNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter DL number"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Voter ID</label>
                    <input
                      type="text"
                      value={formData.voterId || ''}
                      onChange={(e) => handleChange('voterId', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter Voter ID"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Health & Experience */}
            {activeSection === 'health' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Health & Meditation Experience
                </h3>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Osho Meditation Experience</label>
                  <textarea
                    value={formData.oshoMeditationExperience || ''}
                    onChange={(e) => handleChange('oshoMeditationExperience', e.target.value)}
                    style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                    placeholder="Describe your meditation experience"
                  />
                </div>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Health Issues</label>
                  <textarea
                    value={formData.healthIssues || ''}
                    onChange={(e) => handleChange('healthIssues', e.target.value)}
                    style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                    placeholder="Any health issues or concerns"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>COVID Vaccination Status</label>
                    <select
                      value={formData.covidVaccinationStatus || ''}
                      onChange={(e) => handleChange('covidVaccinationStatus', e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select status</option>
                      <option value="NOT_VACCINATED">Not Vaccinated</option>
                      <option value="PARTIALLY_VACCINATED">Partially Vaccinated</option>
                      <option value="FULLY_VACCINATED">Fully Vaccinated</option>
                      <option value="BOOSTED">Boosted</option>
                    </select>
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Courses Attending</label>
                    <input
                      type="text"
                      value={formData.coursesAttending || ''}
                      onChange={(e) => handleChange('coursesAttending', e.target.value)}
                      style={inputStyle}
                      placeholder="List courses"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Arrival Information */}
            {activeSection === 'arrival' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Arrival Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Expected Arrival Date</label>
                    <input
                      type="date"
                      value={formData.expectedArrivalDate || ''}
                      onChange={(e) => handleChange('expectedArrivalDate', e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Expected Arrival Time</label>
                    <input
                      type="time"
                      value={formData.expectedArrivalTime || ''}
                      onChange={(e) => handleChange('expectedArrivalTime', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Payment Information */}
            {activeSection === 'payment' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Payment Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Balance Amount (₹)</label>
                    <input
                      type="number"
                      value={formData.balanceAmount || ''}
                      onChange={(e) => handleChange('balanceAmount', parseFloat(e.target.value) || 0)}
                      style={inputStyle}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Paid Amount (₹)</label>
                    <input
                      type="number"
                      value={formData.paidAmount || ''}
                      onChange={(e) => handleChange('paidAmount', parseFloat(e.target.value) || 0)}
                      style={inputStyle}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Remaining Balance (₹)</label>
                    <input
                      type="number"
                      value={formData.remainingBalance || ''}
                      onChange={(e) => handleChange('remainingBalance', parseFloat(e.target.value) || 0)}
                      style={inputStyle}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Admin Fields */}
            {activeSection === 'admin' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Admin Fields
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Room Number</label>
                    <input
                      type="text"
                      value={formData.roomNumber || ''}
                      onChange={(e) => handleChange('roomNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="e.g., A-101"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Bill Number</label>
                    <input
                      type="text"
                      value={formData.billNumber || ''}
                      onChange={(e) => handleChange('billNumber', e.target.value)}
                      style={inputStyle}
                      placeholder="Enter bill number"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Amount Received (₹)</label>
                    <input
                      type="number"
                      value={formData.amountReceived || ''}
                      onChange={(e) => handleChange('amountReceived', parseFloat(e.target.value) || 0)}
                      style={inputStyle}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>

                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>Form Status</label>
                    <select
                      value={formData.status || ''}
                      onChange={(e) => handleChange('status', e.target.value)}
                      style={inputStyle}
                    >
                      <option value="INCOMPLETE">Incomplete</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="PAYMENT_PENDING">Payment Pending</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="VERIFIED">Verified</option>
                    </select>
                  </div>
                </div>

                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Office Notes</label>
                  <textarea
                    value={formData.officeNotes || ''}
                    onChange={(e) => handleChange('officeNotes', e.target.value)}
                    style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                    placeholder="Internal notes for office use"
                  />
                </div>
              </div>
            )}

            {/* Progress Flags */}
            {activeSection === 'progress' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Progress Flags
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {[
                    { field: 'personalInfoCompleted', label: 'Personal Info Completed' },
                    { field: 'addressInfoCompleted', label: 'Address Info Completed' },
                    { field: 'travelInfoCompleted', label: 'Travel Info Completed' },
                    { field: 'documentsUploaded', label: 'Documents Uploaded' },
                    { field: 'termsAgreed', label: 'Terms Agreed' },
                    { field: 'paymentSubmitted', label: 'Payment Submitted' },
                    { field: 'paymentVerified', label: 'Payment Verified' },
                    { field: 'arrivalTimeProvided', label: 'Arrival Time Provided' },
                    { field: 'otpVerified', label: 'OTP Verified' },
                  ].map(({ field, label }) => (
                    <label key={field} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      backgroundColor: formData[field] ? '#F0F3EE' : '#F5F3EB',
                      border: formData[field] ? '1px solid #1A3E2A' : '1px solid #E3DFD3',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData[field] || false}
                        onChange={() => handleCheckboxChange(field)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: formData[field] ? '#0F2619' : '#36433B'
                      }}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            {activeSection === 'terms' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1A3E2A' }}>
                  Terms & Conditions Acceptance
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  {[
                    { field: 'termsAccepted', label: 'Terms & Conditions Accepted' },
                    { field: 'privacyAccepted', label: 'Privacy Policy Accepted' },
                    { field: 'photoVideoPermission', label: 'Photo/Video Permission Granted' },
                    { field: 'rulesUnderstood', label: 'Property Rules Understood' },
                    { field: 'healthResponsibility', label: 'Health Responsibility Accepted' },
                    { field: 'identityProofProvided', label: 'Identity Proof Provided' },
                  ].map(({ field, label }) => (
                    <label key={field} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px',
                      backgroundColor: formData[field] ? '#F0F3EE' : '#F5F3EB',
                      border: formData[field] ? '1px solid #1A3E2A' : '1px solid #E3DFD3',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData[field] || false}
                        onChange={() => handleCheckboxChange(field)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: formData[field] ? '#0F2619' : '#36433B'
                      }}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer with Save Button */}
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexShrink: 0
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            {saving ? (
              <>
                <span style={{ 
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Saving...
              </>
            ) : (
              <>Save Changes</>
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}

export default EditCheckinFormModal;
